<script setup lang="ts">
    import { Plus } from 'lucide-vue-next';
    import { Button } from '@/components/ui/button';
    import {
        Dialog,
        DialogTrigger,
        DialogContent,
        DialogHeader,
        DialogTitle,
        DialogClose,
        DialogFooter,
        DialogBody,
    } from '@/components/ui/dialog';
    import { toast } from 'vue-sonner';
    import { useI18n } from 'vue-i18n';
    import type { Employee, SharedData } from '@/types';
    import { InputRow } from '@/components/ui/form';
    import { Textarea } from '@/components/ui/textarea';
    import { computed, ref, watch } from 'vue';
    import { useForm, usePage } from '@inertiajs/vue3';
    import { useQueryClient } from '@tanstack/vue-query';
    import QueryKey from '@/enums/QueryKey';

    const props = defineProps<{
        employee: Employee;
        comment: string;
        isApproved: boolean;
        payrollId?: number;
    }>();

    const page = usePage<SharedData>();
    const selectedCompanyId = computed(() => page.props.auth.user.selected_company_id);

    const queryClient = useQueryClient();

    const { t } = useI18n();

    const isOpen = ref(false);

    const buttonText = computed(() => {
        if (!props.isApproved) {
            return props.comment
                ? t('salary.payroll.change_comment')
                : t('salary.payroll.add_comment');
        }

        if (props.comment) {
            return t('salary.payroll.show_comment');
        }
    });

    const form = useForm({
        employeeId: props.employee.id,
        comment: props.comment,
    });

    const updateComment = () => {
        form.post(route('v2.salary.payrolls.add-comment', { payroll: props.payrollId }), {
            preserveScroll: true,
            onSuccess: () => {
                form.resetAndClearErrors();
                toast.success(t('salary.payroll.comment_success'));
                queryClient.invalidateQueries({
                    queryKey: [QueryKey.PAYROLL, selectedCompanyId, props.payrollId],
                });
                isOpen.value = false;
            },
            onError: (error: any) => {
                toast.error(t('forms.system_erro'));
            },
        });
    };

    watch(
        () => props.comment,
        (newVal, oldVal) => {
            if (newVal !== oldVal) {
                form.comment = newVal;
            }
        },
    );
</script>

<template>
    <Dialog
        v-model:open="isOpen"
        v-if="!isApproved || (isApproved && comment)"
    >
        <DialogTrigger as-child>
            <Button
                variant="continue"
                size="xs"
            >
                {{ buttonText }}
                <Plus class="size-3" />
            </Button>
        </DialogTrigger>
        <DialogContent class="sm:max-w-lg">
            <DialogHeader>
                <DialogTitle>{{ t('salary.payroll.add_comment') }}</DialogTitle>
            </DialogHeader>

            <DialogBody>
                <InputRow
                    :form="form"
                    name="comment"
                    label="salary.payroll.comment"
                >
                    <Textarea
                        id="comment"
                        v-model="form.comment"
                        :disabled="isApproved"
                        class="disabled:opacity-100"
                    />
                </InputRow>
            </DialogBody>

            <DialogFooter class="gap-2 border-t sm:justify-center">
                <DialogClose as-child>
                    <Button
                        variant="outline"
                        size="sm"
                    >
                        {{ $t('forms.cancel') }}
                    </Button>
                </DialogClose>

                <Button
                    :disabled="form.processing || isApproved"
                    :loading="form.processing"
                    size="sm"
                    @click="updateComment"
                >
                    {{ $t('salary.payroll.add_comment') }}
                </Button>
            </DialogFooter>
        </DialogContent>
    </Dialog>
</template>
